import { z } from 'zod';
import { difficultySchema, interviewCategorySchema } from '../enums.js';
import { submissionResponseSchema } from './evaluation.js';

/**
 * Interview mode.
 *
 * A session is a fixed set of questions chosen up front, so refreshing the page
 * cannot reshuffle them and the timer has something stable to run against.
 */

export const interviewDifficultySchema = z.union([difficultySchema, z.literal('random')]);
export type InterviewDifficulty = z.infer<typeof interviewDifficultySchema>;

export const startInterviewRequestSchema = z.object({
  difficulty: interviewDifficultySchema.default('random'),
  /** 'any' spans every category. */
  category: z.union([interviewCategorySchema, z.literal('any')]).default('any'),
  questionCount: z.number().int().min(1).max(10).default(5),
});
export type StartInterviewRequest = z.infer<typeof startInterviewRequestSchema>;

export const interviewQuestionSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  promptMd: z.string(),
  starterSql: z.string(),
  difficulty: difficultySchema,
  category: z.string().nullable(),
  datasetSlug: z.string(),
  timeLimitSeconds: z.number().int(),
  /** Populated once the learner has answered this question in this session. */
  answered: z.boolean(),
  correct: z.boolean(),
  elapsedSeconds: z.number().int(),
  /**
   * What the learner submitted. Present so that reviewing a past session shows
   * their own answer back to them — the graded feedback itself is not stored,
   * and a results screen that could only say "answered" would be useless.
   */
  submittedSql: z.string().nullable(),
});
export type InterviewQuestion = z.infer<typeof interviewQuestionSchema>;

export const interviewSessionSchema = z.object({
  id: z.uuid(),
  difficulty: z.string(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  totalCount: z.number().int(),
  correctCount: z.number().int(),
  answeredCount: z.number().int(),
  questions: z.array(interviewQuestionSchema),
});
export type InterviewSession = z.infer<typeof interviewSessionSchema>;

export const interviewAnswerRequestSchema = z.object({
  exerciseId: z.uuid(),
  sql: z.string().min(1).max(10_000),
  /** Client-measured, clamped server-side to the question's limit. */
  elapsedSeconds: z.number().int().min(0).max(7200).default(0),
});
export type InterviewAnswerRequest = z.infer<typeof interviewAnswerRequestSchema>;

export const interviewAnswerResponseSchema = z.object({
  submission: submissionResponseSchema,
  session: interviewSessionSchema,
});
export type InterviewAnswerResponse = z.infer<typeof interviewAnswerResponseSchema>;

export const interviewHistoryEntrySchema = z.object({
  id: z.uuid(),
  difficulty: z.string(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  correctCount: z.number().int(),
  totalCount: z.number().int(),
});
export type InterviewHistoryEntry = z.infer<typeof interviewHistoryEntrySchema>;

export const interviewHistoryResponseSchema = z.object({
  items: z.array(interviewHistoryEntrySchema),
  /** Number of questions in the bank, per category, for the setup screen. */
  available: z.array(z.object({ category: z.string(), count: z.number().int() })),
});
export type InterviewHistoryResponse = z.infer<typeof interviewHistoryResponseSchema>;
